/**
 * Tells a browser visitor about Metrika's cookies, once.
 *
 * Telegram and a build without a counter have no such cookies, so there the
 * notice stays away. The answer lives in a cookie on the parent domain: the
 * landing page shows the same notice, and one "got it" is enough for both.
 */
import { useState } from 'react'
import { usePlatform } from '@/auth/AuthProvider'
import { LegalLink, PRIVACY_URL } from '@/components/LegalLink'
import { t } from '@/i18n'
import { metrikaEnabled } from '@/lib/metrika'

const SEEN = 'cookie_notice=1'

function rememberSeen(): void {
  const domain = location.hostname.endsWith('airoundme.ru') ? '; domain=.airoundme.ru' : ''
  document.cookie = `${SEEN}; max-age=31536000; path=/; samesite=lax${domain}`
}

export function CookieNotice() {
  const platform = usePlatform()
  const [seen, setSeen] = useState(() => document.cookie.includes(SEEN))

  if (seen || platform?.kind !== 'browser' || !metrikaEnabled) return null

  return (
    <aside
      aria-label={t('cookie_notice.label')}
      className="fixed inset-x-4 z-50 mx-auto flex max-w-xl flex-col gap-3 rounded-2xl border border-hairline bg-card p-4 text-sm text-ink-soft shadow-card sm:flex-row sm:items-center"
      style={{ bottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
    >
      <p className="flex-1">{t('cookie_notice.text')}</p>
      <div className="flex items-center justify-between gap-4">
        <LegalLink href={PRIVACY_URL}>{t('cookie_notice.details')}</LegalLink>
        <button
          type="button"
          onClick={() => {
            rememberSeen()
            setSeen(true)
          }}
          className="min-h-10 rounded-full bg-ink-strong px-4 font-medium text-card transition active:scale-[0.98]"
        >
          {t('cookie_notice.ok')}
        </button>
      </div>
    </aside>
  )
}
